import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';
import IconButton from 'src/components/BeaconV2Components/Generic/IconButton';
import { TooltipIcon } from 'src/components/SvgIcons/SvgIcons';
import InfoModal from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/components/InfoModal';

interface ShelfCreditBalanceCardProps {
  width?: number;
  height?: number;
}

const toNum = (v: any): number | null => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/,/g, ''));
    return Number.isNaN(n) ? null : n;
  }
  return null;
};

const ShelfCreditBalanceCard: React.FC<ShelfCreditBalanceCardProps> = ({ width = 168, height = 60 }) => {
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const divisionId = useAppSelector((state) => _get(state, ['user', 'session', 'divisionId'], null));

  // Simple in-memory + sessionStorage cache for credit balance
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const memoryCacheRef = (ShelfCreditBalanceCard as any)._memoryCache || new Map<string, { ts: number; balance: number | null }>();
  (ShelfCreditBalanceCard as any)._memoryCache = memoryCacheRef;

  const makeCacheKey = (divId: string | number | null) => `shelf_credit_balance_${divId ?? 'global'}`;

  const readCache = (key: string) => {
    if (memoryCacheRef.has(key)) return memoryCacheRef.get(key);
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.ts === 'number') {
        memoryCacheRef.set(key, parsed);
        return parsed;
      }
    } catch {}
    return null;
  };

  const writeCache = (key: string, balance: number | null) => {
    const payload = { ts: Date.now(), balance };
    memoryCacheRef.set(key, payload);
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {}
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const cacheKey = makeCacheKey(divisionId);
      const cached = readCache(cacheKey);
      if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
        if (mounted) {
          setCreditBalance(cached.balance);
          setIsLoading(false);
        }
        return;
      }

      try {
        setIsLoading(true);
        const params: any = { env: 'dev' };
        if (divisionId) params.divisionId = divisionId;
        const { data } = await axios.get(buildLlmCrawlerPath('credits'), { params });
        const balance =
          toNum((data || {}).creditBalance) ??
          toNum((data || {}).balance) ??
          toNum((data || {}).remaining) ??
          null;
        if (mounted) {
          setCreditBalance(balance);
          writeCache(cacheKey, balance);
        }
      } catch (e) {
        if (mounted) setCreditBalance(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [divisionId]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 12px',
        width,
        height,
        borderRadius: 8,
        border: '1px solid #E6ECF2',
        background: '#f8f8f9'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, backgroundColor: '#f8f8f9' }}>
        <div
          style={{
            color: 'var(--grays-primary-7-e-8-fa-8, #7E8FA8)',
            backgroundColor: '#f8f8f9',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Roboto',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.96px',
            textTransform: 'uppercase'
          }}
        >
          CREDIT BALANCE
        </div>
        <IconButton onClick={() => setIsInfoOpen(true)} sxProps={{ padding: 0, '&:hover': { backgroundColor: 'transparent' } }}>
          <TooltipIcon width={16} height={16} />
        </IconButton>
      </div>
      <div style={{ fontWeight: 500, fontSize: 16, color: '#0E1E3A' }}>{isLoading ? '—' : creditBalance != null ? creditBalance.toLocaleString() : '—'}</div>
      <InfoModal isOpen={isInfoOpen} setIsOpen={setIsInfoOpen} mode="Generations" />
    </div>
  );
};

export default ShelfCreditBalanceCard;


